import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useFavorites = () => {
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchFavorites = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from("favorite_spots")
      .select("parking_spot_id")
      .eq("user_id", uid);
    setFavoriteIds(new Set((data ?? []).map((r) => r.parking_spot_id)));
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        await fetchFavorites(session.user.id);
      } else {
        setLoading(false);
      }
    };
    init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setUserId(s?.user?.id ?? null);
      if (s?.user) fetchFavorites(s.user.id);
      else setFavoriteIds(new Set());
    });
    return () => subscription.unsubscribe();
  }, [fetchFavorites]);

  const isFavorite = (spotId: string) => favoriteIds.has(spotId);

  const toggleFavorite = async (spotId: string) => {
    if (!userId) {
      toast({ title: "Sign in required", description: "Please sign in to save favorites.", variant: "destructive" });
      return;
    }
    if (favoriteIds.has(spotId)) {
      await supabase.from("favorite_spots").delete().eq("user_id", userId).eq("parking_spot_id", spotId);
      setFavoriteIds((prev) => { const n = new Set(prev); n.delete(spotId); return n; });
      toast({ title: "Removed from favorites" });
    } else {
      const { error } = await supabase.from("favorite_spots").insert({ user_id: userId, parking_spot_id: spotId });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
      setFavoriteIds((prev) => new Set(prev).add(spotId));
      toast({ title: "Added to favorites" });
    }
  };

  return { isFavorite, toggleFavorite, favoriteIds, loading, isAuthenticated: !!userId };
};
